import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/features/auth/store/useAuthStore";
import { useCreateApiKey } from "@/features/tokens/api/token.queries";
import { getPlansByIds, type SubscriptionPlan } from "@/constants/plans";
import {
  createMonitoringIcon,
  MONITORING_BADGE_STYLES,
  MONITORING_TYPES,
  type MonitoringType,
} from "@/constants/monitoring";
import { Spinner } from "@/components/ui/spinner";

/**
 * Determine monitoring type from subscription plan
 */
function getMonitoringTypeFromPlan(plan: SubscriptionPlan): MonitoringType {
  const subscriptionType = plan.subscription_type.toLowerCase();

  if (subscriptionType.includes("influencer")) {
    return MONITORING_TYPES.INFLUENCER;
  }
  if (subscriptionType.includes("pulse")) {
    return MONITORING_TYPES.PULSE;
  }
  if (subscriptionType.includes("keyword")) {
    return MONITORING_TYPES.KEYWORD;
  }
  if (
    subscriptionType.includes("source") ||
    subscriptionType.includes("quadmedia")
  ) {
    return MONITORING_TYPES.SOURCE;
  }
  // if (subscriptionType.includes("post")) {
  //   return MONITORING_TYPES.POST_SEARCH;
  // }

  // Default fallback
  return MONITORING_TYPES.KEYWORD;
}

/**
 * Get badge class name for monitoring type
 */
function getMonitoringBadgeClassName(type: MonitoringType): string {
  const baseClasses =
    "flex h-[28px] w-[28px] items-center justify-center rounded-[6px] p-[3px]";
  const colorClass = MONITORING_BADGE_STYLES[type] || "bg-gray-500";

  return `${baseClasses} ${colorClass}`;
}

export default function TokenForm() {
  const user = useAuthStore((state) => state.user);
  const createApiKeyMutation = useCreateApiKey();

  const [selectedPlanId, setSelectedPlanId] = useState<string>("");

  // Get user's available plans from localStorage
  const userPlans = useMemo(() => {
    if (!user?.plan_ids || user.plan_ids.length === 0) {
      return [];
    }
    return getPlansByIds(user.plan_ids);
  }, [user?.plan_ids]);

  // Get selected plan details for display
  const selectedPlan = useMemo(() => {
    if (!selectedPlanId) return null;
    return userPlans.find((plan) => plan._id === selectedPlanId);
  }, [selectedPlanId, userPlans]);

  // Get selected plan icon and badge
  const selectedPlanDisplay = useMemo(() => {
    if (!selectedPlan) return null;

    const monitoringType = getMonitoringTypeFromPlan(selectedPlan);
    const monitoringIcon = createMonitoringIcon(
      monitoringType,
      "text-sm text-white font-light",
      true,
    );

    return {
      monitoringType,
      monitoringIcon,
    };
  }, [selectedPlan]);

  const handleCreateToken = () => {
    if (!selectedPlanId) {
      return;
    }

    createApiKeyMutation.mutate(selectedPlanId);
  };

  return (
    <div
      className="animate-in slide-in-from-top-2 flex w-full flex-row items-center justify-between rounded-lg bg-white p-5 duration-300"
      style={{
        background:
          "linear-gradient(white, white) padding-box, linear-gradient(to right, #FDBA74, #EA580C) border-box",
        border: "1px solid transparent",
        borderRadius: "8px",
      }}
    >
      <i className="ri-add-line text-lg font-light text-neutral-950"></i>
      <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
        <SelectTrigger className="!h-[48px] min-w-[280px] bg-neutral-100 leading-4 font-semibold -tracking-wide text-neutral-950">
          <SelectValue placeholder="Select plan...">
            {selectedPlan && selectedPlanDisplay && (
              <div className="flex items-center gap-2">
                <div
                  className={getMonitoringBadgeClassName(
                    selectedPlanDisplay.monitoringType,
                  )}
                >
                  {selectedPlanDisplay.monitoringIcon}
                </div>
                <span>{selectedPlan.plan_name}</span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {userPlans.length === 0 ? (
            <SelectItem value="no-plans" disabled>
              No plans available
            </SelectItem>
          ) : (
            userPlans.map((plan) => {
              const monitoringType = getMonitoringTypeFromPlan(plan);
              const monitoringIcon = createMonitoringIcon(
                monitoringType,
                "text-sm text-white",
                true,
              );

              return (
                <SelectItem
                  key={plan._id}
                  value={plan._id}
                  className="h-[44px]"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={getMonitoringBadgeClassName(monitoringType)}
                    >
                      {monitoringIcon}
                    </div>
                    <span className="text-sm leading-4 font-semibold -tracking-wide text-neutral-950">
                      {plan.plan_name}
                    </span>
                  </div>
                </SelectItem>
              );
            })
          )}
        </SelectContent>
      </Select>

      <div className="space-y-1">
        <h1 className="text-xs leading-3 font-medium tracking-normal text-neutral-500">
          QUOTA
        </h1>
        <p className="text-md leading-6 font-medium -tracking-wide text-neutral-950">
          {selectedPlan ? selectedPlan.default_quota?.toLocaleString() : "-"}
        </p>
      </div>
      <div className="space-y-1">
        <h1 className="text-xs leading-3 font-medium tracking-normal text-neutral-500">
          CALLS/MIN
        </h1>
        <p className="text-md leading-6 font-medium -tracking-wide text-neutral-950">
          {selectedPlan ? selectedPlan.calls_per_min.toLocaleString() : "-"}
        </p>
      </div>
      <Button
        className="flex h-[32px] w-[108px] items-center justify-center gap-1 !p-1.5 text-xs leading-normal font-medium hover:scale-none"
        onClick={handleCreateToken}
        disabled={!selectedPlanId || createApiKeyMutation.isPending}
      >
        {createApiKeyMutation.isPending ? (
          <>
            <Spinner size="sm" className="stroke-white!" />
          </>
        ) : createApiKeyMutation.isError ? (
          <>
            <i className="ri-error-warning-line text-md font-light"></i>
            Try Again
          </>
        ) : (
          <>
            <i className="ri-add-line text-md font-light"></i>
            Create token
          </>
        )}
      </Button>
    </div>
  );
}
